import { motion } from 'framer-motion'
import React, { Suspense } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import ProfilePhoto from 'src/shared/assets/images/profile-avatar-fallback.png'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { MapPin } from 'src/shared/components/icons/map-pin'
import { PointIcon } from 'src/shared/components/icons/point-icon'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Skeleton } from 'src/shared/components/ui/miscellaneous/skeleton'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useUserByIdSuspense } from 'src/shared/features/users/api/accounts/use-user-by-id'
import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'
import type { ChatType } from 'src/shared/schemas/chats/chat-data'
import { ChatTypeEnum } from 'src/shared/schemas/chats/chat-data'
import { cn } from 'src/shared/utils/cn'

import CoverPhoto from '../profile-photos/cover-photo'

type ButtonType = 'profile' | 'chat' | 'none'

interface UserCardProps {
  userId?: number
  buttonType?: ButtonType
  className?: string
  clickable?: boolean
  chatId?: string
  chatType?: ChatType
  onClick?: (user: PublicUserData) => void
  isMe?: boolean
  showPoints?: boolean
  showUsername?: boolean
  showAddress?: boolean
  showProfession?: boolean
}

const UserCardContent: React.FC<UserCardProps> = ({
  userId,
  buttonType = 'profile',
  className,
  clickable = false,
  chatId,
  chatType,
  onClick,
  isMe = false,
  showPoints = true,
  showUsername = true,
  showAddress = true,
  showProfession = true,
}) => {
  const navigate = useNavigate()
  const { data: myUserData } = useMyUserData()
  const { data: userData } = useUserByIdSuspense(userId ?? 0)

  const user = isMe ? myUserData : userData

  const fullName = `${user?.firstName} ${user?.lastName}`
  const profession = user?.profession ? professionMap[user.profession].label : ''

  if (!user) {
    return null
  }

  const renderButton = () => {
    switch (buttonType) {
      case 'profile':
        return (
          <Button
            variant="default"
            onClick={() => navigate(paths.users.profile.getHref(user.username))}
            className="mt-4 w-full rounded-full"
          >
            View profile
          </Button>
        )
      case 'chat':
        if (!chatId || !chatType) {
          return null
        }
        switch (chatType) {
          case ChatTypeEnum.REFERRAL:
            return (
              <Button
                onClick={() => navigate(paths.chats.conversation.referral.getHref(chatId))}
                className="mt-4 w-full rounded-full"
              >
                Send Message
              </Button>
            )
          case ChatTypeEnum.PARTNER_MATCH:
            return (
              <Button
                onClick={() => navigate(paths.chats.conversation.match.getHref(chatId))}
                className="mt-4 w-full rounded-full"
              >
                Send Message
              </Button>
            )
          case ChatTypeEnum.NETWORK:
            return (
              <Button
                onClick={() => navigate(paths.chats.conversation.network.getHref(chatId))}
                className="mt-4 w-full rounded-full"
              >
                Send Message
              </Button>
            )
          default:
            return null
        }
      case 'none':
        return null
      default:
        return null
    }
  }

  const handleCardClick = () => {
    if (clickable && user) {
      if (onClick) {
        onClick(user)
      } else {
        navigate(paths.users.profile.getHref(user.username))
      }
    }
  }

  const CardWrapper = clickable ? motion.button : motion.div

  return (
    <CardWrapper
      className={cn(
        'relative z-10 w-full overflow-hidden rounded-3xl bg-card text-left',
        clickable && 'cursor-pointer hover:cursor-pointer',
        className,
      )}
      onClick={handleCardClick}
      whileHover={clickable ? { scale: 1.02, transition: { duration: 0.2 } } : {}}
      whileTap={clickable ? { scale: 0.98 } : {}}
      role={clickable ? 'button' : undefined}
      aria-label={clickable ? `View ${fullName}'s profile` : undefined}
      type={clickable ? 'button' : undefined}
    >
      <div className="relative">
        <CoverPhoto userId={user.id} className="h-14 w-full rounded-t-3xl border object-cover" />
        <div className="absolute left-4 top-3 z-10">
          <ProfileAvatar userId={user.id} size={48} className="border-2 border-card" />
        </div>
        {showPoints && (
          <div className="absolute right-6 top-5 z-30 flex items-center gap-1.5 rounded-lg bg-primary px-2.5 py-1 text-primary-foreground shadow-sm">
            <span className="font-bold">{user.totalPoints}</span>
            <PointIcon className="size-4" />
          </div>
        )}
      </div>
      <div
        className="relative -mt-5 rounded-3xl border bg-card px-4 pb-6 pt-7"
        onClick={(e) => {
          if (!clickable) {
            e.stopPropagation()
          }
        }}
      >
        <div className="flex w-full flex-col items-start justify-between gap-1">
          <div className="flex h-6 flex-row flex-wrap items-center gap-2 gap-y-0 overflow-hidden text-primary">
            <p className="text-left font-bold">{fullName}</p>
            {showUsername && <p className="text-sm text-muted">@{user.username}</p>}
          </div>
          <div className="flex w-full flex-row items-center justify-start gap-x-2 gap-y-1">
            {showProfession && <p className="shrink-0 text-left text-sm text-muted">{profession}</p>}
            {user.shortAddress && showAddress && (
              <div className="flex flex-row items-center gap-1.5">
                <MapPin className="size-3.5 shrink-0 text-muted" />
                <p className={`line-clamp-1 text-sm`}>{user.shortAddress}</p>
              </div>
            )}
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()}>{!clickable && renderButton()}</div>
      </div>
    </CardWrapper>
  )
}

const UserCardPlaceholder = () => {
  return (
    <div className="mb-1 overflow-hidden rounded-3xl bg-card opacity-40">
      <div className="relative">
        <Skeleton className="h-14 w-full rounded-t-3xl border object-cover" />
        <div className="absolute -bottom-2 left-6 z-10">
          <img src={ProfilePhoto} className="size-14 rounded-full border-4 border-card opacity-80" />
        </div>
        <div className="absolute right-6 top-5 z-30 flex items-center gap-1.5 rounded-lg bg-primary px-2.5 py-1 text-primary-foreground opacity-50 shadow-sm">
          <Skeleton className="size-5 bg-primary-foreground" />
          <Skeleton className="h-4 w-8" />
        </div>
        {/* <div className="absolute right-4 top-4 flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5">
          <Skeleton className="size-5" />
          <Skeleton className="h-4 w-8" />
        </div> */}
      </div>
      <div className="relative -mt-5 rounded-3xl border bg-card px-4 pb-6 pt-7">
        <div>
          <Skeleton className="mb-2 h-5 w-48" />
          <Skeleton className="mb-2 h-4 w-32" />
        </div>
      </div>
    </div>
  )
}

export const UserCard: React.FC<UserCardProps> = ({
  userId,
  buttonType = 'profile',
  className,
  clickable = false,
  chatId,
  chatType,
  onClick,
  isMe = false,
  showPoints = true,
  showUsername = true,
  showAddress = true,
  showProfession = true,
}) => {
  return (
    <ErrorBoundaryWrapper title="" description="" fallback={<div />}>
      <Suspense fallback={<UserCardPlaceholder />}>
        <UserCardContent
          userId={userId}
          buttonType={buttonType}
          className={className}
          clickable={clickable}
          chatId={chatId}
          chatType={chatType}
          onClick={onClick}
          isMe={isMe}
          showPoints={showPoints}
          showUsername={showUsername}
          showAddress={showAddress}
          showProfession={showProfession}
        />
      </Suspense>
    </ErrorBoundaryWrapper>
  )
}
